import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { useState, useEffect, useRef } from 'react';
import { array, func, object, oneOfType, node, string, bool, shape, arrayOf, number } from 'prop-types';
import { Search } from '@magalu/stereo-ui-icons';
import { Container, SearchOverlay, DesktopPrefix } from './search-input.styles';
import AutocompleteContainer from './AutocompleteContainer';
import TopTermsContainer from './TopTermsContainer';
import InputContainer from './InputContainer';
import { useOnClickOutside } from '../../utils';

const conditionalInputChange = (desktopMode, term) => {
  return desktopMode && term === '';
};

const getSuggestionsPrefix = desktopMode => desktopMode ? /*#__PURE__*/React.createElement(DesktopPrefix, null, "+") : /*#__PURE__*/React.createElement(Search, {
  color: "#818181"
});

const escKeypressHandler = (event, handler) => {
  // ESC event
  if (event.keyCode === 27) {
    handler();
  }
};

const SearchInput = _ref => {
  let {
    onSearch,
    onTypeChange,
    onInputClear,
    onInputClose,
    onInputOpen,
    onSuggestItemClick,
    onProductItemClick,
    onTopTermItemClick,
    initialValue,
    onClickSearchEvent,
    list,
    products,
    desktopMode,
    topTerms
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, ["onSearch", "onTypeChange", "onInputClear", "onInputClose", "onInputOpen", "onSuggestItemClick", "onProductItemClick", "onTopTermItemClick", "initialValue", "onClickSearchEvent", "list", "products", "desktopMode", "topTerms"]);

  const [term, setTerm] = useState(initialValue);
  const [inputChange, setInputChange] = useState(false);
  const [suggestions, setSuggestions] = useState(list);
  const [productsSuggestions, setProductsSuggestions] = useState(products);
  const suggestionsPrefix = getSuggestionsPrefix(desktopMode);
  const {
    shouldDisplay: shouldDisplayTopTerms,
    list: topTermsList
  } = topTerms;
  useEffect(() => {
    setSuggestions(list || []);
  }, [list]);
  useEffect(() => {
    setProductsSuggestions(products);
  }, [products]);
  useEffect(() => {
    setTerm(initialValue);
  }, [initialValue]);

  const handleSubmit = (e, from) => {
    if (!term.trim()) {
      e.preventDefault();
      return;
    }

    setInputChange(false);
    onSearch(e, term, from);
  };

  const handleType = e => {
    const newTerm = e.target.value; // Allows autocomplete container to keep open for mobile mode only

    setInputChange(!conditionalInputChange(desktopMode, newTerm));
    setTerm(newTerm);
    onTypeChange(newTerm);
  };

  const handleClickSuggest = (e, suggest, from) => {
    onSuggestItemClick(suggest, from);
    setInputChange(false);
    setTerm(suggest);
    onSearch(e, suggest, from);
  };

  const handleClickTopTerm = (e, topTerm) => {
    const {
      term: newTerm
    } = topTerm;
    onTopTermItemClick(topTerm);
    setInputChange(false);
    setTerm(newTerm);
    onSearch(e, newTerm);
  };

  const handleClickProduct = (e, product) => {
    setInputChange(false);
    setTerm('');
    onProductItemClick(e, product);
  };

  const handleClear = () => {
    onInputClear();
    setTerm('');
  };

  const handleClose = () => {
    onInputClose();
    setInputChange(false);
  };

  const handleOpen = () => {
    onInputOpen();
    setInputChange(true);
  }; // Set hook to close autocomplete if user click outside of it
  // https://usehooks.com/useOnClickOutside/


  const autocompleteRef = useRef();
  useOnClickOutside(autocompleteRef, () => {
    if (inputChange || shouldDisplayTopTerms) handleClose(); // send event close only if the autocomplete is opened
  });

  const renderAutocompleteContainer = () => {
    return inputChange && list ? /*#__PURE__*/React.createElement(AutocompleteContainer, {
      desktopMode: desktopMode,
      handleClickProduct: handleClickProduct,
      handleClickSuggest: handleClickSuggest,
      handleClose: handleClose,
      products: products,
      productsSuggestions: productsSuggestions,
      suggestions: suggestions,
      suggestionsPrefix: suggestionsPrefix,
      term: term
    }) : /*#__PURE__*/React.createElement(React.Fragment, null);
  };

  const renderTopTermsContainer = () => {
    return topTermsList ? /*#__PURE__*/React.createElement(TopTermsContainer, _extends({}, topTerms, {
      desktopMode: desktopMode,
      handleClickTopTerm: handleClickTopTerm
    })) : /*#__PURE__*/React.createElement(React.Fragment, null);
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Container, {
    inputChange: inputChange,
    desktopMode: desktopMode,
    ref: autocompleteRef,
    id: "search-container",
    "data-testid": "search-input-container"
  }, /*#__PURE__*/React.createElement(InputContainer, _extends({
    desktopMode: desktopMode,
    handleClear: handleClear,
    handleClose: handleClose,
    handleClickEvent: onClickSearchEvent,
    handleOpen: handleOpen,
    handleSubmit: handleSubmit,
    handleType: handleType,
    inputChange: inputChange,
    keypressHandler: e => escKeypressHandler(e, handleClose),
    term: term
  }, props)), shouldDisplayTopTerms ? renderTopTermsContainer() : renderAutocompleteContainer(), !desktopMode && inputChange && /*#__PURE__*/React.createElement(SearchOverlay, {
    "data-testid": "input-overlay",
    onTouchMove: handleClose,
    onClick: handleClose
  })));
};

process.env.NODE_ENV !== "production" ? SearchInput.propTypes = {
  onSearch: func.isRequired,
  onTypeChange: func.isRequired,
  onInputClear: func,
  onClickSearchEvent: func,
  onSuggestItemClick: func,
  onProductItemClick: func,
  onTopTermItemClick: func,
  onInputClose: func,
  onInputOpen: func,
  initialValue: string,
  list: oneOfType([array, object, node]),
  products: oneOfType([array, object, node]),
  desktopMode: bool,
  topTerms: shape({
    list: arrayOf(shape({
      term: string,
      index: number
    })),
    label: string,
    shouldDisplay: bool
  })
} : void 0;
SearchInput.defaultProps = {
  list: [],
  products: [],
  initialValue: '',
  onInputClear: () => {},
  onSuggestItemClick: () => {},
  onProductItemClick: () => {},
  onTopTermItemClick: () => {},
  onInputClose: () => {},
  onInputOpen: () => {},
  desktopMode: false,
  topTerms: {
    shouldDisplay: false,
    label: 'Mais Buscados',
    list: []
  }
};
export default SearchInput;